import { Loader2, UserMinus } from 'lucide-react';
import { useState } from 'react';

import {
  type FamilyCommunicationLevel,
  type FamilyContactWithParent,
  useEnsureFamily,
  useGetFamilyByStudent,
  useRemoveFamilyContact,
  useSetFamilyBillingParent,
  useUpdateFamilyContact,
} from '~entities/family';
import { useGetParentByStudentId } from '~entities/parent';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import { LinkGuardianSection } from '~features/contacts/family/link-guardian-section';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

interface FamilyGuardiansPanelProps {
  studentId: string;
}

type RemoveBlockReason = 'billing' | 'primary' | null;

interface PendingBilling {
  parentId: string;
  displayName: string;
}

interface PendingPortalOff {
  parentId: string;
  displayName: string;
}

const joinParentName = (contact: FamilyContactWithParent): string => {
  const first = contact.parent?.firstName?.trim() ?? '';
  const last = contact.parent?.lastName?.trim() ?? '';
  return `${first} ${last}`.trim();
};

/** Prefer first+last — Parent.name can lag after portal renames. */
const guardianDisplayName = (contact: FamilyContactWithParent): string => {
  const joined = joinParentName(contact);
  if (joined) return joined;
  const named = contact.parent?.name?.trim();
  if (named) return named;
  return contact.parentId;
};

const resolveRemoveBlockReason = (args: {
  contact: FamilyContactWithParent;
  studentParentId?: string | null;
}): RemoveBlockReason => {
  if (args.contact.isBillingContact) return 'billing';
  if (args.studentParentId && args.contact.parentId === args.studentParentId) {
    return 'primary';
  }
  return null;
};

const removeBlockMessage = (
  reason: Exclude<RemoveBlockReason, null>,
): string => {
  if (reason === 'billing') {
    return 'Set another guardian as billing first, then you can remove this one.';
  }
  return 'This guardian is the primary parent of this student. Turn portal access off instead of removing them.';
};

interface GuardianRowProps {
  contact: FamilyContactWithParent;
  studentParentId?: string | null;
  isBillingPending: boolean;
  isUpdatePending: boolean;
  isRemovePending: boolean;
  onRequestBilling: (contact: FamilyContactWithParent) => void;
  onCommunicationChange: (
    parentId: string,
    level: FamilyCommunicationLevel,
  ) => void;
  onPortalChange: (contact: FamilyContactWithParent, enabled: boolean) => void;
  onRequestRemove: (contact: FamilyContactWithParent) => void;
}

const GuardianRow = ({
  contact,
  studentParentId,
  isBillingPending,
  isUpdatePending,
  isRemovePending,
  onRequestBilling,
  onCommunicationChange,
  onPortalChange,
  onRequestRemove,
}: GuardianRowProps) => {
  const displayName = guardianDisplayName(contact);
  const blockReason = resolveRemoveBlockReason({ contact, studentParentId });
  const canRemove = blockReason === null;
  const removeHint = canRemove
    ? 'Remove from family'
    : removeBlockMessage(blockReason);
  const email = contact.parent?.email?.trim();
  const phone = contact.parent?.primaryPhone?.trim();

  return (
    <div className='space-y-3 border-b border-border pb-4 last:border-b-0'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div className='min-w-0 space-y-1'>
          <p className='truncate font-medium'>{displayName}</p>
          <p className='truncate text-sm text-muted-foreground'>
            {email && email.length > 0 ? email : 'No email'}
          </p>
          {phone && phone.length > 0 ? (
            <p className='text-xs text-muted-foreground'>{phone}</p>
          ) : null}
        </div>

        <div className='flex flex-wrap items-center gap-2'>
          {contact.isBillingContact ? (
            <span className='rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary'>
              Billing
            </span>
          ) : (
            <Button
              size='sm'
              variant='outline'
              disabled={isBillingPending}
              onClick={() => {
                onRequestBilling(contact);
              }}
            >
              Set as billing
            </Button>
          )}

          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex'>
                  <Button
                    size='sm'
                    variant='ghost'
                    className={
                      canRemove
                        ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                        : 'text-muted-foreground/50'
                    }
                    disabled={!canRemove || isRemovePending}
                    aria-label={removeHint}
                    onClick={() => {
                      if (!canRemove) return;
                      onRequestRemove(contact);
                    }}
                  >
                    <UserMinus className='mr-1.5 size-4' />
                    Remove
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className='max-w-xs text-left'>
                <p>{removeHint}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
        <div className='space-y-1.5'>
          <Label className='text-xs text-muted-foreground'>Communication</Label>
          <Select
            value={contact.communicationLevel}
            disabled={isUpdatePending}
            onValueChange={(value) => {
              onCommunicationChange(
                contact.parentId,
                value as FamilyCommunicationLevel,
              );
            }}
          >
            <SelectTrigger className='w-full'>
              <SelectValue placeholder='Communication' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='Full'>Full</SelectItem>
              <SelectItem value='Limited'>Limited</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className='space-y-1.5'>
          <Label className='text-xs text-muted-foreground'>Portal access</Label>
          <Select
            value={contact.portalAccessEnabled ? 'on' : 'off'}
            disabled={isUpdatePending}
            onValueChange={(value) => {
              onPortalChange(contact, value === 'on');
            }}
          >
            <SelectTrigger className='w-full'>
              <SelectValue placeholder='Portal access' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='on'>On</SelectItem>
              <SelectItem value='off'>Off</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {blockReason ? (
        <p className='text-xs text-muted-foreground'>
          {removeBlockMessage(blockReason)}
        </p>
      ) : null}
    </div>
  );
};

export const FamilyGuardiansPanel = ({
  studentId,
}: FamilyGuardiansPanelProps) => {
  const studio = useCurrentStudio();
  const locationId = (studio?.locationId ?? '').trim();
  const { data, isLoading, isError } = useGetFamilyByStudent(studentId);
  const { data: legacyParent } = useGetParentByStudentId(studentId);
  const ensureFamily = useEnsureFamily(studentId);
  const setBilling = useSetFamilyBillingParent(studentId);
  const updateContact = useUpdateFamilyContact(studentId);
  const removeContact = useRemoveFamilyContact(studentId);

  const [pendingRemove, setPendingRemove] =
    useState<FamilyContactWithParent | null>(null);
  const [pendingBilling, setPendingBilling] = useState<PendingBilling | null>(
    null,
  );
  const [pendingPortalOff, setPendingPortalOff] =
    useState<PendingPortalOff | null>(null);

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading family…
      </div>
    );
  }

  if (isError) {
    return (
      <div className='rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive'>
        We could not load family guardians right now. Refresh the page and try
        again. If it keeps happening, contact Enrollio support.
      </div>
    );
  }

  const family = data?.family ?? null;
  const contacts = data?.contacts ?? [];
  const studentParentId = data?.student.parentId ?? null;

  if (!family) {
    return (
      <Card className='max-w-2xl'>
        <CardHeader>
          <CardTitle className='text-lg font-semibold'>
            Family Guardians
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-sm text-muted-foreground'>
            This student is not on a Family yet. Create one from the primary
            parent to manage multiple guardian logins and billing.
          </p>
          <Button
            disabled={!legacyParent?._id || ensureFamily.isPending}
            onClick={() => {
              if (!legacyParent?._id) return;
              ensureFamily.mutate({
                parentId: legacyParent._id,
                displayName: legacyParent.familyName || legacyParent.lastName,
              });
            }}
          >
            {ensureFamily.isPending ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' />
                Creating…
              </>
            ) : (
              'Create Family'
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const closeRemoveDialog = () => {
    if (removeContact.isPending) return;
    setPendingRemove(null);
  };

  const confirmRemove = () => {
    if (!pendingRemove) return;
    removeContact.mutate(
      {
        familyId: family._id,
        parentId: pendingRemove.parentId,
      },
      {
        onSuccess: () => {
          setPendingRemove(null);
        },
      },
    );
  };

  const confirmBilling = () => {
    if (!pendingBilling) return;
    setBilling.mutate(
      {
        familyId: family._id,
        parentId: pendingBilling.parentId,
      },
      {
        onSuccess: () => {
          setPendingBilling(null);
        },
      },
    );
  };

  const confirmPortalOff = () => {
    if (!pendingPortalOff) return;
    updateContact.mutate(
      {
        familyId: family._id,
        parentId: pendingPortalOff.parentId,
        portalAccessEnabled: false,
      },
      {
        onSuccess: () => {
          setPendingPortalOff(null);
        },
      },
    );
  };

  return (
    <>
      <Card className='max-w-2xl'>
        <CardHeader>
          <CardTitle className='text-lg font-semibold'>
            Family Guardians
          </CardTitle>
          <p className='text-sm text-muted-foreground'>
            Real parent logins linked to this household. Billing contact is used
            for bulk charge defaults.
          </p>
        </CardHeader>
        <CardContent className='space-y-6'>
          {contacts.map((contact) => (
            <GuardianRow
              key={contact.parentId}
              contact={contact}
              studentParentId={studentParentId}
              isBillingPending={setBilling.isPending}
              isUpdatePending={updateContact.isPending}
              isRemovePending={removeContact.isPending}
              onRequestBilling={(next) => {
                setPendingBilling({
                  parentId: next.parentId,
                  displayName: guardianDisplayName(next),
                });
              }}
              onCommunicationChange={(parentId, level) => {
                updateContact.mutate({
                  familyId: family._id,
                  parentId,
                  communicationLevel: level,
                });
              }}
              onPortalChange={(next, enabled) => {
                if (!enabled) {
                  setPendingPortalOff({
                    parentId: next.parentId,
                    displayName: guardianDisplayName(next),
                  });
                  return;
                }
                updateContact.mutate({
                  familyId: family._id,
                  parentId: next.parentId,
                  portalAccessEnabled: true,
                });
              }}
              onRequestRemove={(next) => {
                setPendingRemove(next);
              }}
            />
          ))}

          {locationId ? (
            <LinkGuardianSection
              familyId={family._id}
              studentId={studentId}
              locationId={locationId}
              linkedParentIds={contacts.map((contact) => contact.parentId)}
            />
          ) : (
            <p className='text-sm text-muted-foreground'>
              Studio context is missing, so guardians cannot be linked right
              now. Refresh and try again.
            </p>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={pendingBilling !== null}
        onOpenChange={(open) => {
          if (!open && !setBilling.isPending) setPendingBilling(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Set {pendingBilling?.displayName ?? 'this guardian'} as billing?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className='space-y-3 text-sm text-muted-foreground'>
                <p>
                  Bulk charges and enrollments that use the family billing
                  parent will charge{' '}
                  <span className='font-medium text-foreground'>
                    {pendingBilling?.displayName ?? 'this guardian'}
                  </span>{' '}
                  by default.
                </p>
                <p>
                  Individual enrollments can still choose another linked
                  guardian as the payer when needed.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setBilling.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button disabled={setBilling.isPending} onClick={confirmBilling}>
              {setBilling.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Updating…
                </>
              ) : (
                'Set as billing'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingPortalOff !== null}
        onOpenChange={(open) => {
          if (!open && !updateContact.isPending) setPendingPortalOff(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Turn off portal access for{' '}
              {pendingPortalOff?.displayName ?? 'this guardian'}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className='space-y-3 text-sm text-muted-foreground'>
                <p>
                  They will no longer see any students on this family in the
                  parent portal, including attendance, calendars, and makeups.
                </p>
                <p>
                  Their Parent login stays active. You can turn portal access
                  back on at any time.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateContact.isPending}>
              Keep access on
            </AlertDialogCancel>
            <Button
              variant='destructive'
              disabled={updateContact.isPending}
              onClick={confirmPortalOff}
            >
              {updateContact.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Updating…
                </>
              ) : (
                'Turn portal access off'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) closeRemoveDialog();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove{' '}
              {pendingRemove ? guardianDisplayName(pendingRemove) : 'guardian'}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className='space-y-3 text-sm text-muted-foreground'>
                <p>
                  They will lose portal access to every student on this family
                  and stop receiving family emails. Their own Parent login and
                  any students they own stay intact.
                </p>
                <ul className='list-disc space-y-1 pl-4'>
                  <li>Portal access to this household ends immediately</li>
                  <li>Billing and student records are not deleted</li>
                  <li>You can link them again later if needed</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeContact.isPending}>
              Keep guardian
            </AlertDialogCancel>
            <Button
              variant='destructive'
              disabled={removeContact.isPending}
              onClick={confirmRemove}
            >
              {removeContact.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Removing…
                </>
              ) : (
                <>
                  <UserMinus className='mr-2 size-4' />
                  Remove guardian
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
